/**
 * The server-side anti-cheat: it MARKS players for the operators and does nothing else. It never refuses a request,
 * never bans, never takes anything away and never changes a game state: what it decides is a mark in the console
 * (players list badge, the player card, the anticheat module), which an operator reviews ('reviewed_ok') or confirms.
 *
 * Inputs (all after the fact, from the services' listeners; nothing waits for the anti-cheat):
 *  - ProfileService.onEconomy: every stored change of gold / diamonds with its source (./economy.ts income attribution),
 *    and the gaps between the stored balance and the attributed one;
 *  - BattleService.onFinished: every solo battle with the verdict of the re-simulation, the claim and the simulated
 *    outcome, the ticks and the wall-clock time;
 *  - SessionService.onEnded / onInputRefused: online battles (desyncs the server decided, reports against the
 *    simulation, refused input);
 *  - CommandService.onSignal: commands answered from the journal, refused commands, reused opIds;
 *  - its own reconciliation job (tick): the stored balance of recently active players and of a rotating full pass
 *    compared with the anchor the attributed changes left (ProfileRecord.econ).
 * The heuristics and their thresholds: ./anticheat-rules.ts and config.anticheat (docs/security.md explains them).
 *
 * Marks (collection anticheat_marks, id `<playerId>~<rule>`): one per player and rule. A repeat (new evidence, deduped by
 * its key) adds to the count, keeps the newest evidence (config.evidenceKept) and raises the severity if it is higher; a
 * mark an operator reviewed as fine opens again with new evidence (a 'system' history entry); a confirmed one stays
 * confirmed. Operators set the status with a note (resolve; the console audits it). The per-player rolling state
 * (collection anticheat_players: income per hour and source for 7 days, the recent attributed changes, unexplained
 * increases waiting for a spree, the win streak, kills per turn, online desyncs) is cached and written in the
 * background; losing it in a crash loses rates, never invents a mark.
 *
 * Performance: every input updates one player's state (one cached document); the console reads the marks from memory
 * (loaded once at start). The only scan is the reconciliation job's full pass, in batches, in the background.
 */
import type { JsonObject } from '../../../shared/json';
import { DELETED_PLAYER_ID, type PlayerId, type SessionInfo } from '../../../shared/protocol/model';
import { BackendError } from '../../../shared/protocol/errors';
import type { CurrencyPair } from '../config';
import { type StorageBatchOp, deleteOp, putOp } from '../storage';
import {
  CATEGORY_TITLES,
  GAME_INCOME,
  INCOME_CATEGORIES,
  type IncomeCategory,
  MARK_RULES,
  MARK_RULE_IDS,
  type MarkRule,
  type MarkSeverity,
  type MarkStatus,
  type MarkType,
  SPENDING,
  type Text,
  fastFinish,
  incomeCategory,
  inputViolations,
  maxSeverity,
  newStageWinChance,
  outsideEnvelope,
  rateSeverity,
  streakStep,
  text,
  unattributedSeverity,
  violationKind,
} from './anticheat-rules';
import type { BattleFinishFacts, BattleLostFacts } from './battles';
import type { CommandSignal } from './commands';
import { type ProfileRecord, type ServiceDeps, C } from './deps';
import { type EconomyChange, type EconomyGap, gapOf } from './economy';
import { currenciesOf } from './resource-ledger';
import type { SessionEndFacts, SessionInputRefusal } from './sessions';

/** One piece of evidence of a mark: what happened, when, the numbers and the ids the console links to. */
export interface MarkEvidence {
  /** Dedupe key (the same occurrence seen twice counts once). */
  key: string;
  at: number;
  kind: 'balance' | 'change' | 'rate' | 'spend' | 'command' | 'battle' | 'session' | 'import';
  text: Text;
  gold: number | null;
  diam: number | null;
  battleId: string | null;
  replayId: string | null;
  sessionId: string | null;
  ledgerId: string | null;
  opId: string | null;
  revision: number | null;
  /** The time window the evidence covers (rates, sprees, gaps). */
  from: number | null;
  to: number | null;
  data: JsonObject | null;
}

export interface MarkHistoryEntry {
  at: number;
  status: MarkStatus;
  /** The operator's username, or 'system' (a reviewed mark opened again by new evidence). */
  by: string;
  operatorId: string | null;
  role: string | null;
  note: string;
}

export interface AnticheatMark {
  markId: string;
  playerId: PlayerId;
  type: MarkType;
  rule: MarkRule;
  severity: MarkSeverity;
  status: MarkStatus;
  /** The latest occurrence in words, with its numbers. */
  reason: Text;
  firstSeenAt: number;
  lastSeenAt: number;
  /** Occurrences (distinct evidence keys). */
  count: number;
  /** The time span the evidence covers. */
  window: { from: number; to: number };
  /** The newest evidence first (at most config.evidenceKept). */
  evidence: MarkEvidence[];
  /** Evidence keys seen (bounded): an occurrence seen again does not count twice. */
  seen: string[];
  history: MarkHistoryEntry[];
  reviewedAt: number | null;
  reviewedBy: string | null;
}

/** A stored change as the player card's attribution table shows it. */
export interface AttributedChange {
  at: number;
  category: IncomeCategory;
  op: string | null;
  gold: number;
  diam: number;
  revision: number;
  opId: string | null;
  ledgerId: string | null;
  battleId: string | null;
  replayId: string | null;
  verdict: string | null;
  purchaseId: string | null;
}

interface UnexplainedIncrease {
  key: string;
  at: number;
  gold: number;
  diam: number;
  spentGold: number;
  spentDiam: number;
  flagged: boolean;
}

/** The anti-cheat's rolling state of one player (collection anticheat_players). */
export interface PlayerWatch {
  playerId: PlayerId;
  updatedAt: number;
  /** Per hour (start, epoch ms) and category: [gold in, gold out, diamonds in, diamonds out]; 7 days. */
  hours: { h: number; c: Record<string, [number, number, number, number]> }[];
  /** Income of the game's own sources within the rate window: [at, gold, diamonds, category]. */
  recent: [number, number, number, string][];
  /** The last attributed changes, newest first. */
  changes: AttributedChange[];
  unexplained: UnexplainedIncrease[];
  streak: { score: number; wins: number; since: number | null; flagged: boolean };
  kpt: { battleId: string; kpt: number; replayId: string }[];
  desyncs: { at: number; sessionId: string }[];
  refusals: { at: number; sessionId: string }[];
}

/** The attribution summary of a player (console). */
export interface AttributionRow {
  category: IncomeCategory | 'unattributed';
  title: Text;
  goldIn: number;
  goldOut: number;
  diamIn: number;
  diamOut: number;
}

export interface MarkSummary {
  /** Open marks. */
  open: number;
  /** The highest severity among the open marks (null: none open). */
  severity: MarkSeverity | null;
  total: number;
}

export interface MarkActor {
  operatorId: string | null;
  username: string;
  role: string;
}

export interface AnticheatOptions {
  /** A battle simulator is configured (without one every battle is unverified: no unverified-reward marks). */
  simulator: boolean;
}

const HOUR = 3600_000;
const DAY = 24 * HOUR;
const KEEP_HOURS_MS = 7 * DAY;
const CHANGES_KEPT = 30;
const SEEN_KEPT = 200;
const HISTORY_KEPT = 50;
const RECENT_KEPT = 5000;
const CACHE_MAX = 5000;
const PERSIST_EVERY_MS = 5_000;
const COUNTERS_MAX = 20_000;

const pos = (n: number) => (n > 0 ? n : 0);
const iso = (ms: number) => new Date(ms).toISOString().replace('.000Z', 'Z');
const meets = (v: CurrencyPair, min: CurrencyPair) => (v.gold > 0 && v.gold >= min.gold) || (v.diam > 0 && v.diam >= min.diam);
const amountText = (v: CurrencyPair) => ({
  en: [v.gold ? `${v.gold} gold` : '', v.diam ? `${v.diam} diamonds` : ''].filter(Boolean).join(' and ') || 'nothing',
  uk: [v.gold ? `${v.gold} золота` : '', v.diam ? `${v.diam} алмазів` : ''].filter(Boolean).join(' і ') || 'нічого',
});

function emptyWatch(playerId: PlayerId, now: number): PlayerWatch {
  return { playerId, updatedAt: now, hours: [], recent: [], changes: [], unexplained: [], streak: { score: 0, wins: 0, since: null, flagged: false }, kpt: [], desyncs: [], refusals: [] };
}

type NewEvidence = Partial<MarkEvidence> & Pick<MarkEvidence, 'key' | 'at' | 'kind' | 'text'>;

function evidenceOf(e: NewEvidence): MarkEvidence {
  return {
    key: e.key,
    at: e.at,
    kind: e.kind,
    text: { en: e.text.en, uk: e.text.uk },
    gold: e.gold ?? null,
    diam: e.diam ?? null,
    battleId: e.battleId ?? null,
    replayId: e.replayId ?? null,
    sessionId: e.sessionId ?? null,
    ledgerId: e.ledgerId ?? null,
    opId: e.opId ?? null,
    revision: e.revision ?? null,
    from: e.from ?? null,
    to: e.to ?? null,
    data: e.data ?? null,
  };
}

export class AnticheatService {
  /** playerId -> rule -> mark (every mark; loaded at start). */
  private readonly marks = new Map<PlayerId, Map<MarkRule, AnticheatMark>>();
  private readonly watches = new Map<PlayerId, PlayerWatch>();
  private readonly dirtyWatches = new Set<PlayerId>();
  private readonly pending = new Set<Promise<void>>();
  /** Deleted accounts: nothing more is recorded about them. */
  private readonly forgotten = new Set<PlayerId>();
  /** Players with stored changes since the last reconciliation run. */
  private readonly active = new Set<PlayerId>();
  /** Command signal counters: key -> times (in memory only: short windows). */
  private readonly counters = new Map<string, number[]>();
  private loading: Promise<void> | null = null;
  /** The running background job (tick), and the write of the per-player states in it (persist). */
  private ticking: Promise<void> | null = null;
  private persisting: Promise<void> | null = null;
  private lastPersistAt = 0;
  private lastReconcileAt = Number.NEGATIVE_INFINITY;
  private lastPassAt = Number.NEGATIVE_INFINITY;
  private pass: { ids: PlayerId[]; pos: number } | null = null;

  constructor(
    private readonly d: ServiceDeps,
    private readonly opts: AnticheatOptions,
  ) {}

  get enabled(): boolean {
    return this.d.config.anticheat.enabled;
  }

  private get cfg() {
    return this.d.config.anticheat;
  }

  /** Loads the marks (once; BackendCore.create calls it, every reader waits for it). */
  init(): Promise<void> {
    if (!this.loading) {
      this.loading = (async () => {
        if (!this.enabled) return;
        for (const m of await this.d.storage.list<AnticheatMark>(C.anticheatMarks)) {
          if (!m || typeof m.playerId !== 'string' || !(m.rule in MARK_RULES)) continue;
          this.index(m);
        }
      })();
    }
    return this.loading;
  }

  /** An operator restored a damaged mark (admin server.repairDocument): the marks, loaded without it, show it. */
  async repaired(m: AnticheatMark): Promise<void> {
    await this.init();
    if (this.enabled && m && typeof m.playerId === 'string' && m.rule in MARK_RULES) this.index(m);
  }

  /** Resolves when every queued input and the running background job have been processed (tests, shutdown). */
  async idle(): Promise<void> {
    while (this.pending.size > 0 || this.ticking) await Promise.allSettled([...this.pending, ...(this.ticking ? [this.ticking] : [])]);
  }

  private index(m: AnticheatMark): void {
    let byRule = this.marks.get(m.playerId);
    if (!byRule) this.marks.set(m.playerId, (byRule = new Map()));
    byRule.set(m.rule, m);
  }

  /** Runs `fn` for the player in order with its other inputs (the player's anti-cheat lock); errors are logged. */
  private enqueue(playerId: PlayerId, what: string, fn: () => Promise<void>): void {
    if (!this.enabled || playerId === DELETED_PLAYER_ID || this.forgotten.has(playerId)) return;
    const p = this.d.mutex
      .run(`anticheat:${playerId}`, async () => {
        await this.init();
        if (this.forgotten.has(playerId)) return;
        await fn();
      })
      .catch((err) => this.d.logger.error('anti-cheat input failed', { playerId, what, err }));
    this.pending.add(p);
    void p.finally(() => this.pending.delete(p));
  }

  // ---------------------------------------------------------------------------------------------- inputs

  /** ProfileService.onEconomy. */
  economy(c: EconomyChange): void {
    if (!this.enabled) return;
    this.active.add(c.playerId);
    this.enqueue(c.playerId, 'economy', () => this.onEconomy(c));
  }

  /** BattleService.onFinished. */
  battle(f: BattleFinishFacts): void {
    this.enqueue(f.playerId, 'battle', () => this.onBattle(f));
  }

  /** BattleService.onLost: a battle surrendered, replaced or abandoned (a loss without battle.finish). */
  battleLost(f: BattleLostFacts): void {
    this.enqueue(f.playerId, 'battle lost', () => this.onBattleLost(f));
  }

  /** SessionService.onEnded. */
  session(info: SessionInfo, facts: SessionEndFacts): void {
    for (const p of info.participants) this.enqueue(p.playerId, 'session', () => this.onSession(p.playerId, info, facts));
  }

  /** SessionService.onInputRefused. */
  sessionRefusal(r: SessionInputRefusal): void {
    this.enqueue(r.playerId, 'session refusal', () => this.onRefusal(r));
  }

  /** CommandService.onSignal. */
  command(s: CommandSignal): void {
    this.enqueue(s.playerId, 'command', () => this.onCommand(s));
  }

  // ---------------------------------------------------------------------------------------------- state

  private async watch(playerId: PlayerId): Promise<PlayerWatch> {
    let w = this.watches.get(playerId);
    if (!w) {
      w = (await this.d.storage.get<PlayerWatch>(C.anticheatPlayers, playerId)) ?? emptyWatch(playerId, this.d.clock.now());
      // Documents of an older shape get the fields they lack.
      w = { ...emptyWatch(playerId, w.updatedAt ?? 0), ...w };
      this.watches.set(playerId, w);
      this.evict();
    }
    return w;
  }

  private touch(w: PlayerWatch, now: number): void {
    w.updatedAt = now;
    this.dirtyWatches.add(w.playerId);
  }

  /** Keeps the cache bounded (players written to storage first are dropped, oldest first). */
  private evict(): void {
    if (this.watches.size <= CACHE_MAX) return;
    for (const id of this.watches.keys()) {
      if (this.watches.size <= CACHE_MAX) break;
      if (!this.dirtyWatches.has(id)) this.watches.delete(id);
    }
  }

  /** Writes the changed per-player states. */
  async persist(): Promise<void> {
    const run = this.persistNow();
    this.persisting = run;
    try {
      await run;
    } finally {
      if (this.persisting === run) this.persisting = null;
    }
  }

  private async persistNow(): Promise<void> {
    const ids = [...this.dirtyWatches];
    this.dirtyWatches.clear();
    for (let i = 0; i < ids.length; i += 200) {
      const ops: StorageBatchOp[] = [];
      for (const id of ids.slice(i, i + 200)) {
        const w = this.watches.get(id);
        if (w && !this.forgotten.has(id)) ops.push(putOp(C.anticheatPlayers, id, w));
      }
      try {
        if (ops.length > 0) await this.d.storage.batch(ops);
      } catch (err) {
        for (const o of ops) this.dirtyWatches.add(o.id);
        this.d.logger.error('anti-cheat state not stored', { err });
        return;
      }
    }
  }

  // ---------------------------------------------------------------------------------------------- marks

  /**
   * Adds an occurrence to the player's mark of `rule` (creating it). An evidence key seen before changes nothing, except
   * that `upgrade` replaces that evidence when the new severity is higher (a rate that kept growing in its window).
   */
  private async raise(playerId: PlayerId, rule: MarkRule, o: { severity: MarkSeverity; reason: Text; evidence: NewEvidence; upgrade?: boolean }): Promise<void> {
    if (this.forgotten.has(playerId)) return;
    const ev = evidenceOf(o.evidence);
    const current = this.marks.get(playerId)?.get(rule) ?? null;
    const m: AnticheatMark = current
      ? (JSON.parse(JSON.stringify(current)) as AnticheatMark)
      : {
          markId: `${playerId}~${rule}`,
          playerId,
          type: MARK_RULES[rule].type,
          rule,
          severity: o.severity,
          status: 'open',
          reason: o.reason,
          firstSeenAt: ev.at,
          lastSeenAt: ev.at,
          count: 0,
          window: { from: ev.from ?? ev.at, to: ev.to ?? ev.at },
          evidence: [],
          seen: [],
          history: [],
          reviewedAt: null,
          reviewedBy: null,
        };
    if (m.seen.includes(ev.key)) {
      if (!o.upgrade || maxSeverity(m.severity, o.severity) === m.severity) return;
      m.evidence = [ev, ...m.evidence.filter((e) => e.key !== ev.key)].slice(0, this.cfg.evidenceKept);
    } else {
      m.count++;
      m.seen = [...m.seen, ev.key].slice(-SEEN_KEPT);
      m.evidence = [ev, ...m.evidence].slice(0, this.cfg.evidenceKept);
      if (m.status === 'reviewed_ok') {
        m.status = 'open';
        m.history = [...m.history, { at: ev.at, status: 'open' as MarkStatus, by: 'system', operatorId: null, role: null, note: 'new evidence after the review' }].slice(-HISTORY_KEPT);
      }
    }
    m.severity = maxSeverity(m.severity, o.severity);
    m.reason = o.reason;
    m.lastSeenAt = Math.max(m.lastSeenAt, ev.at);
    m.firstSeenAt = Math.min(m.firstSeenAt, ev.at);
    m.window = { from: Math.min(m.window.from, ev.from ?? ev.at), to: Math.max(m.window.to, ev.to ?? ev.at) };
    await this.d.storage.put(C.anticheatMarks, m.markId, m);
    // The account was deleted while this was stored: forget() removes the document after this input, nothing stays.
    if (this.forgotten.has(playerId)) return;
    this.index(m);
    this.d.logger.warn('anti-cheat mark', { playerId, rule, severity: m.severity, count: m.count, status: m.status, evidence: ev.key });
  }

  // ---------------------------------------------------------------------------------------------- resources

  private async onEconomy(c: EconomyChange): Promise<void> {
    const cfg = this.cfg.resources;
    const w = await this.watch(c.playerId);
    // The first state a client makes (L0 / L1: the client creates its profile) is a starting capital, not income.
    const first = c.before === null && c.source.kind === 'client_save';
    const category = first ? 'start' : incomeCategory(c.source);
    const delta = { gold: (c.after?.gold ?? 0) - (c.before?.gold ?? 0), diam: (c.after?.diam ?? 0) - (c.before?.diam ?? 0) };
    if (c.gap) await this.gapFound(w, c.gap, c.at, 'write');
    this.record(w, c, category, delta);
    const s = first ? { ...c.source, dev: this.d.config.devProfiles } : c.source;
    const ids = {
      revision: c.revision,
      opId: s.kind === 'command' ? (s.ref ?? null) : null,
      ledgerId: s.kind === 'admin' ? (s.ref ?? null) : null,
      battleId: s.battleId ?? null,
      replayId: s.replayId ?? null,
    };

    // Without a readable state before the change there is nothing to compare with (except a new profile's capital).
    const excess = c.before !== null || category === 'start' ? outsideEnvelope(category, s, delta) : null;
    if (excess && meets(excess, cfg.unattributedMin)) {
      const key = `rev:${c.revision}`;
      const amount = { gold: excess.gold, diam: excess.diam };
      const a = amountText(amount);
      await this.raise(c.playerId, 'outside_source', {
        severity: unattributedSeverity(amount, cfg.unattributedHigh),
        reason: text(
          `${a.en} more than ${category}${s.op ? ` (${s.op})` : ''} can give at revision ${c.revision} (${iso(c.at)}): ${excess.why.en}.`,
          `${a.uk} понад те, що може дати ${category}${s.op ? ` (${s.op})` : ''}, у ревізії ${c.revision} (${iso(c.at)}): ${excess.why.uk}.`,
        ),
        evidence: { key, at: c.at, kind: 'change', text: excess.why, gold: amount.gold, diam: amount.diam, ...ids, data: { category, op: s.op ?? null, source: s.kind, deltaGold: delta.gold, deltaDiam: delta.diam } },
      });
      this.unexplained(w, key, c.at, amount);
    }

    if (category === 'battle' && s.op === 'battle.finish' && s.verdict && s.verdict !== 'verified' && this.opts.simulator && (delta.gold > 0 || delta.diam > 0)) {
      const a = amountText({ gold: pos(delta.gold), diam: pos(delta.diam) });
      await this.raise(c.playerId, 'unverified_reward', {
        severity: 'medium',
        reason: text(
          `Battle ${s.battleId ?? '?'} gave ${a.en} although its verification ended '${s.verdict}' (${iso(c.at)}).`,
          `Бій ${s.battleId ?? '?'} дав ${a.uk}, хоча перевірка завершилася '${s.verdict}' (${iso(c.at)}).`,
        ),
        evidence: { key: `battle:${s.battleId ?? c.revision}`, at: c.at, kind: 'battle', text: text(`verdict ${s.verdict}`, `вердикт ${s.verdict}`), gold: pos(delta.gold), diam: pos(delta.diam), ...ids, data: { verdict: s.verdict } },
      });
    }

    if (GAME_INCOME.has(category) && c.before !== null && (delta.gold > 0 || delta.diam > 0)) await this.rate(w, c, category, delta);
    if (SPENDING.has(category)) await this.spending(w, c, { gold: pos(-delta.gold), diam: pos(-delta.diam) }, ids);

    if (category === 'import' && c.after && (c.after.gold >= cfg.importWarn.gold || c.after.diam >= cfg.importWarn.diam)) {
      await this.raise(c.playerId, 'import_balance', {
        severity: 'low',
        reason: text(
          `An imported save brought a balance of ${c.after.gold} gold and ${c.after.diam} diamonds (${iso(c.at)}): imported states are made by the client.`,
          `Імпортоване збереження принесло баланс ${c.after.gold} золота й ${c.after.diam} алмазів (${iso(c.at)}): імпортований стан створює клієнт.`,
        ),
        evidence: { key: `import:${c.revision}`, at: c.at, kind: 'import', text: text('imported balance', 'імпортований баланс'), gold: c.after.gold, diam: c.after.diam, revision: c.revision },
      });
    }
    this.touch(w, c.at);
  }

  /** The hourly attribution buckets, the recent changes and the rate window. */
  private record(w: PlayerWatch, c: EconomyChange, category: IncomeCategory, delta: CurrencyPair): void {
    const h = Math.floor(c.at / HOUR) * HOUR;
    let bucket = w.hours.find((b) => b.h === h);
    if (!bucket) {
      bucket = { h, c: {} };
      w.hours = [...w.hours.filter((b) => b.h > c.at - KEEP_HOURS_MS), bucket].sort((a, b) => a.h - b.h);
    }
    const cell = bucket.c[category] ?? [0, 0, 0, 0];
    cell[0] += pos(delta.gold);
    cell[1] += pos(-delta.gold);
    cell[2] += pos(delta.diam);
    cell[3] += pos(-delta.diam);
    bucket.c[category] = cell;
    const s = c.source;
    const row: AttributedChange = {
      at: c.at,
      category,
      op: s.op ?? null,
      gold: delta.gold,
      diam: delta.diam,
      revision: c.revision,
      opId: s.kind === 'command' ? (s.ref ?? null) : null,
      ledgerId: s.kind === 'admin' ? (s.ref ?? null) : null,
      battleId: s.battleId ?? null,
      replayId: s.replayId ?? null,
      verdict: s.verdict ?? null,
      purchaseId: s.kind === 'purchase' || s.kind === 'refund' ? (s.ref ?? null) : null,
    };
    w.changes = [row, ...w.changes].slice(0, CHANGES_KEPT);
  }

  /** An increase that no known source made (a gap of the anchor): the mark and a candidate for a spree. */
  private async gapFound(w: PlayerWatch, g: EconomyGap, at: number, how: 'write' | 'reconcile'): Promise<void> {
    const cfg = this.cfg.resources;
    const inc = { gold: pos(g.delta.gold), diam: pos(g.delta.diam) };
    if (!meets(inc, cfg.unattributedMin)) return;
    const key = `gap:${g.sinceRevision}:${g.sinceAt}`;
    const a = amountText(inc);
    await this.raise(w.playerId, 'unattributed', {
      severity: unattributedSeverity(inc, cfg.unattributedHigh),
      reason: text(
        `${a.en} appeared after revision ${g.sinceRevision} (${iso(g.sinceAt)}) without any known source: the stored balance is ${g.found.gold} gold / ${g.found.diam} diamonds, the attributed changes leave ${g.expected.gold} / ${g.expected.diam} (found ${how === 'write' ? 'at the next write' : 'by the reconciliation job'}, ${iso(at)}).`,
        `${a.uk} з'явилися після ревізії ${g.sinceRevision} (${iso(g.sinceAt)}) без жодного відомого джерела: збережений баланс ${g.found.gold} золота / ${g.found.diam} алмазів, а атрибутовані зміни дають ${g.expected.gold} / ${g.expected.diam} (знайдено ${how === 'write' ? 'під час наступного запису' : 'завданням звірки'}, ${iso(at)}).`,
      ),
      evidence: {
        key,
        at,
        kind: 'balance',
        text: text(`balance ${g.found.gold} / ${g.found.diam}, expected ${g.expected.gold} / ${g.expected.diam}`, `баланс ${g.found.gold} / ${g.found.diam}, очікувано ${g.expected.gold} / ${g.expected.diam}`),
        gold: inc.gold,
        diam: inc.diam,
        revision: g.revision,
        from: g.sinceAt,
        to: at,
        data: { sinceRevision: g.sinceRevision, expectedGold: g.expected.gold, expectedDiam: g.expected.diam, foundGold: g.found.gold, foundDiam: g.found.diam, foundBy: how },
      },
    });
    this.unexplained(w, key, at, inc);
    this.touch(w, at);
  }

  private unexplained(w: PlayerWatch, key: string, at: number, amount: CurrencyPair): void {
    if (w.unexplained.some((u) => u.key === key) || !meets(amount, this.cfg.resources.spreeMin)) return;
    const keep = w.unexplained.filter((u) => at - u.at <= this.cfg.resources.spreeWindowMs);
    w.unexplained = [...keep, { key, at, gold: amount.gold, diam: amount.diam, spentGold: 0, spentDiam: 0, flagged: false }].slice(-50);
  }

  /** Income of the game's own sources over the rate window against config.maxGameIncome. */
  private async rate(w: PlayerWatch, c: EconomyChange, category: IncomeCategory, delta: CurrencyPair): Promise<void> {
    const cfg = this.cfg.resources;
    const from = c.at - cfg.rateWindowMs;
    w.recent = [...w.recent.filter((r) => r[0] > from), [c.at, pos(delta.gold), pos(delta.diam), category] as [number, number, number, string]].slice(-RECENT_KEPT);
    const income = { gold: 0, diam: 0 };
    const by: JsonObject = {};
    for (const [, gold, diam, cat] of w.recent) {
      income.gold += gold;
      income.diam += diam;
      const cur = (by[cat] as { gold: number; diam: number } | undefined) ?? { gold: 0, diam: 0 };
      by[cat] = { gold: cur.gold + gold, diam: cur.diam + diam };
    }
    const severity = rateSeverity(income, cfg.maxGameIncome);
    if (!severity) return;
    const slot = Math.floor(c.at / cfg.rateWindowMs);
    const a = amountText(income);
    const minutes = Math.round(cfg.rateWindowMs / 60_000);
    await this.raise(c.playerId, 'income_rate', {
      severity,
      upgrade: true,
      reason: text(
        `${a.en} from the game's own sources within ${minutes} min (${iso(from)} - ${iso(c.at)}); the model's limit is ${cfg.maxGameIncome.gold} gold / ${cfg.maxGameIncome.diam} diamonds.`,
        `${a.uk} з власних джерел гри за ${minutes} хв (${iso(from)} - ${iso(c.at)}); межа моделі — ${cfg.maxGameIncome.gold} золота / ${cfg.maxGameIncome.diam} алмазів.`,
      ),
      evidence: { key: `rate:${slot}`, at: c.at, kind: 'rate', text: text(`income per ${minutes} min`, `дохід за ${minutes} хв`), gold: income.gold, diam: income.diam, from, to: c.at, data: by },
    });
  }

  /** Spending (shop, auction, exchange) within the spree window after an unexplained increase. */
  private async spending(w: PlayerWatch, c: EconomyChange, spent: CurrencyPair, ids: Partial<MarkEvidence>): Promise<void> {
    if (spent.gold <= 0 && spent.diam <= 0) return;
    const cfg = this.cfg.resources;
    for (const u of w.unexplained) {
      if (u.flagged || c.at - u.at > cfg.spreeWindowMs || c.at < u.at) continue;
      u.spentGold += spent.gold;
      u.spentDiam += spent.diam;
      const goldSpree = u.gold > 0 && u.spentGold >= cfg.spreeShare * u.gold;
      const diamSpree = u.diam > 0 && u.spentDiam >= cfg.spreeShare * u.diam;
      if (!goldSpree && !diamSpree) continue;
      u.flagged = true;
      const got = amountText({ gold: u.gold, diam: u.diam });
      const used = amountText({ gold: u.spentGold, diam: u.spentDiam });
      const minutes = Math.round((c.at - u.at) / 60_000);
      await this.raise(c.playerId, 'spree', {
        severity: 'high',
        reason: text(
          `${used.en} spent within ${minutes} min after ${got.en} of unknown origin appeared (${iso(u.at)} - ${iso(c.at)}).`,
          `${used.uk} витрачено за ${minutes} хв після появи ${got.uk} невідомого походження (${iso(u.at)} - ${iso(c.at)}).`,
        ),
        evidence: {
          key: `spree:${u.key}`,
          at: c.at,
          kind: 'spend',
          text: text(`spent after the increase ${u.key}`, `витрати після збільшення ${u.key}`),
          gold: u.spentGold,
          diam: u.spentDiam,
          ...ids,
          from: u.at,
          to: c.at,
          data: { increase: u.key, increaseGold: u.gold, increaseDiam: u.diam },
        },
      });
    }
    w.unexplained = w.unexplained.filter((u) => c.at - u.at <= cfg.spreeWindowMs);
  }

  private count(key: string, at: number, windowMs: number): number {
    const times = (this.counters.get(key) ?? []).filter((t) => t > at - windowMs);
    times.push(at);
    this.counters.delete(key);
    this.counters.set(key, times);
    if (this.counters.size > COUNTERS_MAX) {
      const first = this.counters.keys().next().value;
      if (first !== undefined) this.counters.delete(first);
    }
    return times.length;
  }

  private async onCommand(s: CommandSignal): Promise<void> {
    const cfg = this.cfg.resources.replays;
    const minutes = Math.round(cfg.windowMs / 60_000);
    const slot = Math.floor(s.at / cfg.windowMs);
    if (s.kind === 'journal_replay') {
      const n = this.count(`${s.playerId}|op|${s.opId}`, s.at, cfg.windowMs);
      if (n < cfg.sameOpId) return;
      await this.raise(s.playerId, 'command_replays', {
        severity: 'low',
        reason: text(
          `The command ${s.op} (opId ${s.opId}) was sent ${n} times within ${minutes} min (${iso(s.at)}): every repeat got the journaled answer.`,
          `Команду ${s.op} (opId ${s.opId}) надіслано ${n} разів за ${minutes} хв (${iso(s.at)}): кожен повтор отримав відповідь із журналу.`,
        ),
        evidence: { key: `replay:${s.opId}:${slot}`, at: s.at, kind: 'command', text: text(`${n} repeats of one opId`, `${n} повторів одного opId`), opId: s.opId, data: { op: s.op, repeats: n } },
      });
    } else if (s.kind === 'refused') {
      // A battle log the game's recorder cannot produce (overlapping commands: more than one action in a tick, ticks out
      // of order or beyond the battle's limit): input of a client that is not the game's.
      if (s.op === 'battle.finish' && s.reason === 'bad_args' && s.field?.startsWith('log')) {
        await this.raise(s.playerId, 'impossible_input', {
          severity: 'medium',
          reason: text(
            `A battle log the game cannot produce was sent (${iso(s.at)}): ${s.problem ?? 'invalid log'}. The game's recorder never writes such a log.`,
            `Надіслано журнал бою, якого гра не може створити (${iso(s.at)}): ${s.problem ?? 'недійсний журнал'}. Записувач гри такого журналу не пише.`,
          ),
          evidence: { key: `log:${s.opId}`, at: s.at, kind: 'battle', text: text(s.problem ?? 'invalid log', s.problem ?? 'недійсний журнал'), opId: s.opId, data: { field: s.field, problem: s.problem ?? null } },
        });
      }
      const n = this.count(`${s.playerId}|refused|${s.hash}`, s.at, cfg.windowMs);
      if (n < cfg.refusedSame) return;
      await this.raise(s.playerId, 'command_replays', {
        severity: 'low',
        reason: text(
          `The same refused command ${s.op} (${s.reason ?? 'refused'}) was sent ${n} times within ${minutes} min (${iso(s.at)}).`,
          `Ту саму відхилену команду ${s.op} (${s.reason ?? 'відхилено'}) надіслано ${n} разів за ${minutes} хв (${iso(s.at)}).`,
        ),
        evidence: { key: `refused:${s.hash}:${slot}`, at: s.at, kind: 'command', text: text(`${n} refusals of one command`, `${n} відмов однієї команди`), opId: s.opId, data: { op: s.op, reason: s.reason ?? null, repeats: n } },
      });
    } else {
      const n = this.count(`${s.playerId}|reused`, s.at, cfg.windowMs);
      if (n < cfg.opIdReused) return;
      await this.raise(s.playerId, 'command_replays', {
        severity: 'medium',
        reason: text(
          `${n} opIds were reused for other commands within ${minutes} min (${iso(s.at)}): an opId names one command.`,
          `${n} opId використано повторно для інших команд за ${minutes} хв (${iso(s.at)}): opId позначає одну команду.`,
        ),
        evidence: { key: `reused:${s.opId}:${slot}`, at: s.at, kind: 'command', text: text('opId reused for another command', 'opId повторно для іншої команди'), opId: s.opId, data: { op: s.op, reuses: n } },
      });
    }
  }

  // ---------------------------------------------------------------------------------------------- battles

  private async onBattle(f: BattleFinishFacts): Promise<void> {
    const cfg = this.cfg.battles;
    const w = await this.watch(f.playerId);
    const at = f.finishedAt;
    const ids = { battleId: f.battleId, replayId: f.replayId };
    const where = `${f.mode} ${f.stage}`;
    if (f.abandoned) {
      if (f.newStage) this.resetStreak(w);
      this.touch(w, at);
      return;
    }
    if (f.simError === 'invalid_record') {
      const v = inputViolations(f.simMessage);
      const kinds = [...new Set(v.map(violationKind))];
      const what = kinds.includes('weapon') ? text('a weapon not in the arsenal or not available yet', 'зброю, якої немає в арсеналі або ще не можна взяти') : kinds.includes('click') ? text('a map click the interface could not make', 'клік по мапі, якого не міг зробити інтерфейс') : text('input the interface could not make', 'введення, якого не міг зробити інтерфейс');
      await this.raise(f.playerId, 'impossible_input', {
        severity: 'high',
        reason: text(`Battle ${f.battleId} (${where}, ${iso(at)}) holds ${what.en}: ${v.join('; ') || f.simMessage || ''}.`, `Бій ${f.battleId} (${where}, ${iso(at)}) містить ${what.uk}: ${v.join('; ') || f.simMessage || ''}.`),
        evidence: { key: `battle:${f.battleId}`, at, kind: 'battle', text: text(v[0] ?? 'invalid record', v[0] ?? 'недійсний запис'), ...ids, data: { violations: v, mode: f.mode, stage: f.stage } },
      });
    } else if (f.simError === 'max_ticks') {
      await this.raise(f.playerId, 'endless_battle', {
        severity: 'medium',
        reason: text(`Battle ${f.battleId} (${where}, ${iso(at)}) does not end within the battle's tick limit in the server's simulation.`, `Бій ${f.battleId} (${where}, ${iso(at)}) не закінчується в межах ліміту тактів у симуляції сервера.`),
        evidence: { key: `battle:${f.battleId}`, at, kind: 'battle', text: text('beyond the tick limit', 'понад ліміт тактів'), ...ids, data: { message: f.simMessage ?? null } },
      });
    }
    if (f.verdict === 'rejected' && f.simulated && f.differs.length > 0) {
      const claim = f.claim as JsonObject;
      const sim = f.simulated as JsonObject;
      const diff: JsonObject = {};
      for (const k of f.differs) diff[k] = { claimed: claim[k] ?? null, simulated: sim[k] ?? null };
      const winner = f.differs.includes('winner');
      await this.raise(f.playerId, 'sim_mismatch', {
        severity: winner ? 'high' : 'medium',
        reason: text(
          `Battle ${f.battleId} (${where}, ${iso(at)}): the client reported ${f.differs.map((k) => `${k} ${JSON.stringify(claim[k] ?? null)}`).join(', ')}, the server's simulation gives ${f.differs.map((k) => `${k} ${JSON.stringify(sim[k] ?? null)}`).join(', ')}.`,
          `Бій ${f.battleId} (${where}, ${iso(at)}): клієнт повідомив ${f.differs.map((k) => `${k} ${JSON.stringify(claim[k] ?? null)}`).join(', ')}, симуляція сервера дає ${f.differs.map((k) => `${k} ${JSON.stringify(sim[k] ?? null)}`).join(', ')}.`,
        ),
        evidence: { key: `battle:${f.battleId}`, at, kind: 'battle', text: text(`differs: ${f.differs.join(', ')}`, `розбіжність: ${f.differs.join(', ')}`), ...ids, data: diff },
      });
    }
    const elapsed = f.finishedAt - f.startedAt;
    const fast = fastFinish(f.ticks, elapsed, cfg);
    if (fast) {
      await this.raise(f.playerId, 'fast_finish', {
        severity: fast.severity,
        reason: text(
          `Battle ${f.battleId} (${where}) ran ${f.ticks} battle ticks (${Math.round(fast.minimumMs / 1000)} s at 50 ticks per second) but was finished ${Math.round(elapsed / 1000)} s after its start (${iso(f.startedAt)} - ${iso(at)}).`,
          `Бій ${f.battleId} (${where}) тривав ${f.ticks} тактів (${Math.round(fast.minimumMs / 1000)} с при 50 тактах за секунду), але завершився через ${Math.round(elapsed / 1000)} с після початку (${iso(f.startedAt)} - ${iso(at)}).`,
        ),
        evidence: { key: `battle:${f.battleId}`, at, kind: 'battle', text: text(`${Math.round(elapsed / 1000)} s for ${f.ticks} ticks`, `${Math.round(elapsed / 1000)} с на ${f.ticks} тактів`), ...ids, from: f.startedAt, to: at, data: { ticks: f.ticks, elapsedMs: elapsed, minimumMs: Math.round(fast.minimumMs), simulatedTicks: f.simulatedTicks } },
      });
    }
    // Streaks and kill rates of battles the server trusts (a rejected one is lost anyway).
    if (f.verdict !== 'rejected' && f.newStage && newStageWinChance(f.mode, f.stage) !== null) {
      if (f.win) {
        if (w.streak.score === 0) w.streak.since = at;
        w.streak.score += streakStep(f.mode, f.stage);
        w.streak.wins++;
        if (w.streak.score >= cfg.streakScore && !w.streak.flagged) {
          w.streak.flagged = true;
          await this.raise(f.playerId, 'win_streak', {
            severity: 'low',
            reason: text(
              `${w.streak.wins} new stages won in a row without a loss since ${iso(w.streak.since ?? at)} (the last: ${where}); under the economy model's win chances such a streak has a probability of about 1e-${Math.floor(w.streak.score)}.`,
              `${w.streak.wins} нових етапів поспіль без поразки з ${iso(w.streak.since ?? at)} (останній: ${where}); за шансами перемоги моделі економіки ймовірність такої серії близько 1e-${Math.floor(w.streak.score)}.`,
            ),
            evidence: { key: `streak:${w.streak.since ?? at}`, at, kind: 'battle', text: text(`${w.streak.wins} wins in a row`, `${w.streak.wins} перемог поспіль`), ...ids, from: w.streak.since ?? at, to: at, data: { wins: w.streak.wins, score: Math.round(w.streak.score * 100) / 100 } },
          });
        }
      } else this.resetStreak(w);
    }
    const kc = cfg.killsPerTurn;
    if (f.win && f.verdict === 'verified' && f.kills >= kc.minKills && f.annihilators === 0 && f.ownTurns > 0) {
      w.kpt = [...w.kpt, { battleId: f.battleId, kpt: f.kills / f.ownTurns, replayId: f.replayId }].slice(-kc.battles);
      const mean = w.kpt.reduce((s, x) => s + x.kpt, 0) / w.kpt.length;
      if (w.kpt.length >= kc.battles && mean >= kc.mean) {
        const battles = w.kpt.map((x) => x.battleId);
        w.kpt = [];
        await this.raise(f.playerId, 'kills_per_turn', {
          severity: 'low',
          reason: text(
            `${Math.round(mean * 10) / 10} enemy kills per own turn on average over the last ${battles.length} won battles (each with at least ${kc.minKills} kills, no Annihilator); the model's limit is ${kc.mean}.`,
            `У середньому ${Math.round(mean * 10) / 10} вбитих ворогів за власний хід в останніх ${battles.length} виграних боях (у кожному щонайменше ${kc.minKills} вбивств, без Аннігілятора); межа моделі — ${kc.mean}.`,
          ),
          evidence: { key: `kpt:${f.battleId}`, at, kind: 'battle', text: text(`mean ${Math.round(mean * 10) / 10} kills per turn`, `у середньому ${Math.round(mean * 10) / 10} вбивств за хід`), ...ids, data: { battles, mean: Math.round(mean * 100) / 100 } },
        });
      }
    }
    this.touch(w, at);
  }

  /** A loss without battle.finish: the surrender button, a battle replaced or abandoned. A new stage lost ends a streak. */
  private async onBattleLost(f: BattleLostFacts): Promise<void> {
    if (!f.newStage) return;
    const w = await this.watch(f.playerId);
    if (w.streak.wins === 0 && w.streak.score === 0) return;
    this.resetStreak(w);
    this.touch(w, f.at);
  }

  private resetStreak(w: PlayerWatch): void {
    w.streak = { score: 0, wins: 0, since: null, flagged: false };
  }

  private async onSession(playerId: PlayerId, info: SessionInfo, facts: SessionEndFacts): Promise<void> {
    const out = info.outcome;
    if (!out) return;
    const at = out.endedAt;
    const ids = { sessionId: info.sessionId, replayId: out.replayId };
    const others = info.participants.filter((p) => p.playerId !== playerId);
    if (info.online && out.diverged?.includes(playerId) && out.diverged.length < info.participants.length) {
      const cfg = this.cfg.battles.desync;
      const w = await this.watch(playerId);
      w.desyncs = [...w.desyncs.filter((x) => x.at > at - cfg.windowMs && x.sessionId !== info.sessionId), { at, sessionId: info.sessionId }];
      const n = w.desyncs.length;
      const days = Math.round(cfg.windowMs / DAY);
      await this.raise(playerId, 'online_desync', {
        severity: n >= cfg.high ? 'high' : 'medium',
        reason: text(
          `Online battle ${info.sessionId} (${iso(at)}): the server's simulation contradicted this player's checkpoints or turn ends (${out.reason ?? 'desync'}); ${n} such battle(s) within ${days} days, while ${others.map((p) => p.displayName).join(', ') || 'the other side'} stayed in sync.`,
          `Онлайн-бій ${info.sessionId} (${iso(at)}): симуляція сервера суперечить контрольним точкам або завершенням ходів цього гравця (${out.reason ?? 'розсинхронізація'}); таких боїв за ${days} днів: ${n}, тоді як ${others.map((p) => p.displayName).join(', ') || 'інша сторона'} лишилися синхронними.`,
        ),
        evidence: { key: `session:${info.sessionId}`, at, kind: 'session', text: text(out.reason ?? 'desync', out.reason ?? 'розсинхронізація'), ...ids, data: { diverged: out.diverged.length, inWindow: n } },
      });
      this.touch(w, at);
      return;
    }
    if (out.verdict !== 'rejected') return;
    if (!info.online) {
      // Only a result the player reported itself: a session the server ended (forfeited, abandoned, by an operator)
      // was checked against the server's own "nobody won", nothing the player claimed.
      if (facts.forced || !facts.reports.some((r) => r.playerId === playerId)) return;
      await this.raise(playerId, 'sim_mismatch', {
        severity: 'medium',
        reason: text(`Battle session ${info.sessionId} (${info.mode} ${info.stage}, ${iso(at)}) was rejected by the server's check: ${out.reason ?? 'rejected'}.`, `Бойову сесію ${info.sessionId} (${info.mode} ${info.stage}, ${iso(at)}) відхилила перевірка сервера: ${out.reason ?? 'відхилено'}.`),
        evidence: { key: `session:${info.sessionId}`, at, kind: 'session', text: text(out.reason ?? 'rejected', out.reason ?? 'відхилено'), ...ids },
      });
      return;
    }
    const sim = facts.simulated;
    const mine = facts.reports.find((r) => r.playerId === playerId);
    if (!sim || !mine) return;
    const wrong = (r: { winnerTeam: number; finalHash: string | null }) => r.winnerTeam !== sim.winnerTeam || (r.finalHash !== null && r.finalHash !== sim.finalHash);
    // Only one side contradicts the simulation: when every report does, the cause may be the server's.
    if (!wrong(mine) || facts.reports.every(wrong)) return;
    const myTeams = info.participants.find((p) => p.playerId === playerId)?.teams ?? [];
    const claimedOwnWin = myTeams.includes(mine.winnerTeam) && !myTeams.includes(sim.winnerTeam);
    await this.raise(playerId, 'online_result', {
      severity: claimedOwnWin ? 'high' : 'medium',
      reason: text(
        `Online battle ${info.sessionId} (${iso(at)}): this player reported winner team ${mine.winnerTeam}${mine.finalHash !== sim.finalHash ? ' and another final state' : ''}, the server's simulation gives team ${sim.winnerTeam}; the other side reported the simulated result.`,
        `Онлайн-бій ${info.sessionId} (${iso(at)}): гравець повідомив переможця — команду ${mine.winnerTeam}${mine.finalHash !== sim.finalHash ? ' та інший кінцевий стан' : ''}, симуляція сервера дає команду ${sim.winnerTeam}; інша сторона повідомила симульований результат.`,
      ),
      evidence: { key: `session:${info.sessionId}`, at, kind: 'session', text: text(`reported team ${mine.winnerTeam}, simulated ${sim.winnerTeam}`, `повідомлено команду ${mine.winnerTeam}, симуляція ${sim.winnerTeam}`), ...ids, data: { reportedWinner: mine.winnerTeam, simulatedWinner: sim.winnerTeam, hashDiffers: mine.finalHash !== null && mine.finalHash !== sim.finalHash } },
    });
  }

  private async onRefusal(r: SessionInputRefusal): Promise<void> {
    const w = await this.watch(r.playerId);
    const windowMs = this.cfg.battles.desync.windowMs;
    w.refusals = [...w.refusals.filter((x) => x.at > r.at - windowMs), { at: r.at, sessionId: r.sessionId }].slice(-100);
    const n = w.refusals.length;
    await this.raise(r.playerId, 'online_input', {
      severity: n >= 3 ? 'medium' : 'low',
      reason: text(
        `Online battle ${r.sessionId}: input of tick ${r.tick} refused (${r.reason}) at ${iso(r.at)}; ${n} refusal(s) within ${Math.round(windowMs / DAY)} days. The game's own client never sends such input.`,
        `Онлайн-бій ${r.sessionId}: введення такту ${r.tick} відхилено (${r.reason}) о ${iso(r.at)}; відмов за ${Math.round(windowMs / DAY)} днів: ${n}. Власний клієнт гри такого введення не надсилає.`,
      ),
      evidence: { key: `refusal:${r.sessionId}:${r.tick}`, at: r.at, kind: 'session', text: text(r.reason, r.reason), sessionId: r.sessionId, data: { tick: r.tick, reason: r.reason } },
    });
    this.touch(w, r.at);
  }

  // ---------------------------------------------------------------------------------------------- the reconciliation job

  /**
   * The background work (BackendCore.tick, which does not wait for it: one run at a time, its errors are logged): the
   * changed per-player states are written every few seconds; every config.reconcile.intervalMs the stored balance of
   * up to `batch` players active since the last run (the rest wait for the next run) plus the next `batch` of the
   * rotating full pass is compared with its anchor (a gap: an increase no known source made). So a run reads at most
   * 2 x `batch` profiles, however many players are active.
   */
  tick(now = this.d.clock.now()): Promise<void> {
    if (!this.enabled) return Promise.resolve();
    if (this.ticking) return this.ticking;
    const run = this.runTick(now)
      .catch((err) => this.d.logger.error('anti-cheat background job failed', { err }))
      .finally(() => {
        if (this.ticking === run) this.ticking = null;
      });
    this.ticking = run;
    return run;
  }

  private async runTick(now: number): Promise<void> {
    await this.init();
    if (now - this.lastPersistAt >= PERSIST_EVERY_MS) {
      this.lastPersistAt = now;
      await this.persist();
    }
    const rc = this.cfg.reconcile;
    if (now - this.lastReconcileAt < rc.intervalMs) return;
    this.lastReconcileAt = now;
    const ids = new Set<PlayerId>();
    for (const id of this.active) {
      if (ids.size >= rc.batch) break;
      ids.add(id);
    }
    for (const id of ids) this.active.delete(id);
    if (!this.pass && now - this.lastPassAt >= rc.fullPassMs) {
      this.lastPassAt = now;
      // The ids from the accounts (small documents); each profile is read on its own, a batch per run.
      const all = await this.d.storage.list<{ playerId?: unknown }>(C.accounts);
      this.pass = { ids: all.map((a) => a?.playerId).filter((id): id is string => typeof id === 'string'), pos: 0 };
    }
    if (this.pass) {
      const chunk = this.pass.ids.slice(this.pass.pos, this.pass.pos + rc.batch);
      this.pass.pos += chunk.length;
      for (const id of chunk) ids.add(id);
      if (this.pass.pos >= this.pass.ids.length) this.pass = null;
    }
    // One profile that cannot be read (a damaged document) must not stop the others of the run.
    for (const id of ids) await this.reconcileOne(id, now).catch((err) => this.d.logger.warn('anti-cheat reconciliation of a player skipped', { playerId: id, err }));
  }

  /** Compares one player's stored balance with its anchor (no lock: the anchor is in the same document). */
  async reconcileOne(playerId: PlayerId, now = this.d.clock.now()): Promise<void> {
    if (this.forgotten.has(playerId)) return;
    const rec = await this.d.storage.get<ProfileRecord>(C.profiles, playerId);
    if (!rec?.econ || !rec.profile.gameState) return;
    const gap = gapOf(rec.econ, currenciesOf(rec.profile.gameState), rec.profile.revision);
    if (!gap) return;
    this.enqueue(playerId, 'reconcile', async () => this.gapFound(await this.watch(playerId), gap, now, 'reconcile'));
  }

  /** Writes what is pending (shutdown, backups). */
  async flush(): Promise<void> {
    if (!this.enabled) return;
    await this.idle();
    await this.persist();
  }

  // ---------------------------------------------------------------------------------------------- reading (console)

  /** The open marks of a player in short (players list). */
  summaryOf(playerId: PlayerId): MarkSummary {
    const marks = [...(this.marks.get(playerId)?.values() ?? [])];
    let severity: MarkSeverity | null = null;
    let open = 0;
    for (const m of marks) {
      if (m.status !== 'open') continue;
      open++;
      severity = severity ? maxSeverity(severity, m.severity) : m.severity;
    }
    return { open, severity, total: marks.length };
  }

  /** Every mark of a player, newest first. */
  marksOf(playerId: PlayerId): AnticheatMark[] {
    return [...(this.marks.get(playerId)?.values() ?? [])].sort((a, b) => b.lastSeenAt - a.lastSeenAt || (a.rule < b.rule ? -1 : 1));
  }

  /** Every mark (console list). */
  allMarks(): AnticheatMark[] {
    const out: AnticheatMark[] = [];
    for (const byRule of this.marks.values()) out.push(...byRule.values());
    return out;
  }

  getMark(markId: string): AnticheatMark | null {
    const i = markId.lastIndexOf('~');
    if (i <= 0) return null;
    const rule = markId.slice(i + 1);
    return (this.marks.get(markId.slice(0, i))?.get(rule as MarkRule) as AnticheatMark | undefined) ?? null;
  }

  /** An operator's decision on a mark (with a note; the console audits the action). */
  async resolve(markId: string, status: MarkStatus, note: string, actor: MarkActor): Promise<AnticheatMark> {
    await this.init();
    const found = this.getMark(markId);
    if (!found) throw new BackendError('NOT_FOUND', 'No such anti-cheat mark', { reason: 'mark_not_found', markId });
    return this.d.mutex.run(`anticheat:${found.playerId}`, async () => {
      const cur = this.getMark(markId);
      if (!cur) throw new BackendError('NOT_FOUND', 'No such anti-cheat mark', { reason: 'mark_not_found', markId });
      const now = this.d.clock.now();
      const m = JSON.parse(JSON.stringify(cur)) as AnticheatMark;
      m.status = status;
      m.reviewedAt = now;
      m.reviewedBy = actor.username;
      m.history = [...m.history, { at: now, status, by: actor.username, operatorId: actor.operatorId, role: actor.role, note }].slice(-HISTORY_KEPT);
      await this.d.storage.put(C.anticheatMarks, m.markId, m);
      this.index(m);
      this.d.logger.info('anti-cheat mark resolved', { playerId: m.playerId, rule: m.rule, status, operator: actor.username });
      return m;
    });
  }

  /** Where the player's gold and diamonds came from over the last 24 hours and 7 days, and the recent changes. */
  async attribution(playerId: PlayerId, now = this.d.clock.now()): Promise<{ day: AttributionRow[]; week: AttributionRow[]; changes: AttributedChange[] }> {
    const w = this.watches.get(playerId) ?? (await this.d.storage.get<PlayerWatch>(C.anticheatPlayers, playerId));
    const sum = (from: number): AttributionRow[] => {
      const rows = new Map<string, AttributionRow>();
      for (const b of w?.hours ?? []) {
        if (b.h + HOUR <= from) continue;
        for (const [cat, [gi, go, di, dout]] of Object.entries(b.c)) {
          const r = rows.get(cat) ?? { category: cat as IncomeCategory, title: CATEGORY_TITLES[cat as IncomeCategory] ?? text(cat, cat), goldIn: 0, goldOut: 0, diamIn: 0, diamOut: 0 };
          r.goldIn += gi;
          r.goldOut += go;
          r.diamIn += di;
          r.diamOut += dout;
          rows.set(cat, r);
        }
      }
      // Increases from no known source, from the marks' evidence in the period.
      const gaps = this.marks.get(playerId)?.get('unattributed')?.evidence.filter((e) => e.at >= from) ?? [];
      if (gaps.length > 0) {
        rows.set('unattributed', {
          category: 'unattributed',
          title: CATEGORY_TITLES.unattributed,
          goldIn: gaps.reduce((s, e) => s + (e.gold ?? 0), 0),
          goldOut: 0,
          diamIn: gaps.reduce((s, e) => s + (e.diam ?? 0), 0),
          diamOut: 0,
        });
      }
      const order = [...INCOME_CATEGORIES, 'unattributed'];
      return [...rows.values()].sort((a, b) => order.indexOf(a.category) - order.indexOf(b.category));
    };
    return { day: sum(now - DAY), week: sum(now - 7 * DAY), changes: [...(w?.changes ?? [])] };
  }

  // ---------------------------------------------------------------------------------------------- accounts

  /** The player's marks and state (account deletion). */
  async deletionOps(playerId: PlayerId): Promise<StorageBatchOp[]> {
    await this.init();
    const ops = this.marksOf(playerId).map((m) => deleteOp(C.anticheatMarks, m.markId));
    if (this.watches.has(playerId) || (await this.d.storage.get(C.anticheatPlayers, playerId))) ops.push(deleteOp(C.anticheatPlayers, playerId));
    return ops;
  }

  /** After an account deletion: nothing more is recorded, and what a running input stored is removed after it. */
  forget(playerId: PlayerId): void {
    if (!this.enabled) return;
    this.forgotten.add(playerId);
    this.marks.delete(playerId);
    this.watches.delete(playerId);
    this.dirtyWatches.delete(playerId);
    this.active.delete(playerId);
    const p = this.d.mutex
      .run(`anticheat:${playerId}`, async () => {
        // After every input of the player that was already running (the same lock): what one of them stored or put in
        // memory goes too. A mark's id is `<playerId>~<rule>`, so no scan of the other players' marks is needed.
        this.marks.delete(playerId);
        this.watches.delete(playerId);
        this.dirtyWatches.delete(playerId);
        // A write of the per-player states that started before the deletion may still store this player's.
        if (this.persisting) await this.persisting.catch(() => undefined);
        const ops: StorageBatchOp[] = [];
        if (await this.d.storage.get(C.anticheatPlayers, playerId)) ops.push(deleteOp(C.anticheatPlayers, playerId));
        for (const rule of MARK_RULE_IDS) {
          const id = `${playerId}~${rule}`;
          if (await this.d.storage.get(C.anticheatMarks, id)) ops.push(deleteOp(C.anticheatMarks, id));
        }
        if (ops.length > 0) await this.d.storage.batch(ops);
      })
      .catch((err) => this.d.logger.error('anti-cheat data of a deleted account not removed', { playerId, err }));
    this.pending.add(p);
    void p.finally(() => this.pending.delete(p));
  }
}
